import React, { useState, useRef } from "react";
import { UploadSimple, Copy, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { toast } from "sonner";

/**
 * Base64FileEncoder — drag-and-drop helper that converts a small binary file
 * (Apple .p12 cert, Google service-account .json) into a base64 string
 * ready to paste into the Emergent Deploy Panel as an env var. Runs ENTIRELY
 * in the browser — the file never leaves the user's device. The base64
 * string is auto-copied to clipboard with one click.
 *
 * Props:
 *   - label: human-friendly title (e.g. "Apple .p12 cert")
 *   - accept: file input `accept` attribute (e.g. ".p12,.pfx")
 *   - envVar: the env var name to display (e.g. "APPLE_PASS_CERT_P12_B64")
 *   - maxBytes: refuse uploads larger than this (default 200KB — these
 *     credential files are always tiny; large input is a red flag).
 *   - validateJson: if true, ensure the file parses as JSON before encoding
 *     (used for Google service-account JSON to catch corrupt downloads).
 */
export default function Base64FileEncoder({
  label,
  accept,
  envVar,
  maxBytes = 200 * 1024,
  validateJson = false,
}) {
  const [b64, setB64] = useState("");
  const [filename, setFilename] = useState("");
  const [fileSize, setFileSize] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    setError("");
    setB64("");
    if (!file) return;
    if (file.size > maxBytes) {
      setError(`File is ${(file.size / 1024).toFixed(0)} KB — expected under ${(maxBytes / 1024).toFixed(0)} KB. Are you sure you grabbed the right file?`);
      return;
    }
    setBusy(true);
    try {
      const buf = await file.arrayBuffer();
      // Convert ArrayBuffer → base64 in chunks to avoid call-stack overflow.
      const bytes = new Uint8Array(buf);
      let bin = "";
      const CHUNK = 0x8000;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
      }
      const encoded = btoa(bin);

      if (validateJson) {
        try {
          const txt = new TextDecoder("utf-8").decode(bytes);
          const parsed = JSON.parse(txt);
          // For Google service-account JSON, do a light sanity check.
          if (!parsed.client_email || !parsed.private_key) {
            setError("This JSON file is missing `client_email` or `private_key` — that's not a Google service-account key.");
            setBusy(false);
            return;
          }
        } catch (e) {
          setError("This file is not valid JSON. Make sure you downloaded the JSON key (not the .p12 variant).");
          setBusy(false);
          return;
        }
      }

      setB64(encoded);
      setFilename(file.name);
      setFileSize(file.size);
    } catch (e) {
      setError(`Could not read file: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };
  const onDragOver = (e) => { e.preventDefault(); };
  const onPick = () => inputRef.current?.click();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(b64);
      toast.success(`Copied ${b64.length} chars to clipboard`);
    } catch {
      toast.error("Clipboard access denied — select the text and copy manually.");
    }
  };

  const reset = () => {
    setB64("");
    setFilename("");
    setFileSize(0);
    setError("");
  };

  return (
    <div className="mt-3 rounded-xl border border-[#E5E0D8] bg-white p-4" data-testid={`b64-encoder-${envVar}`}>
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">In-browser base64 encoder · {label}</p>
      <p className="text-[11px] text-[#4A5C50] mt-1 leading-relaxed">
        Drag your file here (or click to pick). It never leaves your browser — the base64 string is copied locally and you paste it into the Deploy Panel manually.
      </p>

      {!b64 && !error && (
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onClick={onPick}
          className="mt-3 rounded-lg border-2 border-dashed border-[#E5E0D8] hover:border-[#184A30] hover:bg-[#184A30]/5 transition cursor-pointer py-6 text-center"
          data-testid={`b64-encoder-${envVar}-dropzone`}
        >
          <UploadSimple size={24} className="mx-auto text-[#4A5C50]" />
          <p className="text-xs text-[#4A5C50] mt-1">
            {busy ? "Encoding…" : "Drop file or click to browse"}
          </p>
          <p className="text-[10px] text-[#4A5C50] mt-0.5">Accept: <span className="mono">{accept}</span></p>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`b64-encoder-${envVar}-input`}
      />

      {error && (
        <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-900 text-xs p-3 flex items-start gap-2" data-testid={`b64-encoder-${envVar}-error`}>
          <XCircle size={14} weight="fill" className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium">Encoding failed</p>
            <p className="mt-0.5">{error}</p>
            <button onClick={reset} className="mt-2 text-rose-800 hover:underline text-xs font-medium">Try another file</button>
          </div>
        </div>
      )}

      {b64 && !error && (
        <div className="mt-3 space-y-2">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <p className="text-[11px] text-[#4A5C50] inline-flex items-center gap-1.5">
              <CheckCircle size={12} weight="fill" className="text-emerald-600" />
              <span className="font-medium text-emerald-800">{filename}</span>
              <span className="text-[#4A5C50]">·</span>
              <span>{(fileSize / 1024).toFixed(1)} KB</span>
              <span className="text-[#4A5C50]">·</span>
              <span>{b64.length.toLocaleString()} chars base64</span>
            </p>
            <button onClick={reset} className="text-[11px] text-[#4A5C50] hover:text-[#112318] underline">Pick another file</button>
          </div>
          <div className="rounded-lg border border-[#E5E0D8] bg-[#F4F0EA] p-3">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50] mb-1">Env var name</p>
            <p className="mono text-xs text-[#112318] font-semibold">{envVar}</p>
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50] mt-3 mb-1">Value (paste this whole string)</p>
            <textarea
              readOnly
              value={b64}
              className="w-full font-mono text-[10px] bg-white border border-[#E5E0D8] rounded p-2 h-24 resize-y break-all"
              onFocus={(e) => e.target.select()}
              data-testid={`b64-encoder-${envVar}-output`}
            />
            <Button
              size="sm"
              onClick={copy}
              className="mt-2 bg-[#184A30] hover:bg-[#0F3320] text-white text-xs"
              data-testid={`b64-encoder-${envVar}-copy`}
            >
              <Copy size={12} className="mr-1" /> Copy to clipboard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
